import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, addDoc, setDoc, serverTimestamp, doc } from 'firebase/firestore';
import { Container, Form, Button, Alert, Spinner, Card, Image, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const VideoUploadPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'field_report',
    author: 'Al-Majd Media',
    duration: '',
  });
  const [thumbnail, setThumbnail] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [isArabic, setIsArabic] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const navigate = useNavigate();

  const translations = {
    'Video Upload': 'رفع فيديو',
    'Upload your video content': 'رفع محتوى الفيديو',
    'Title *': 'العنوان *',
    'Enter video title': 'أدخل عنوان الفيديو',
    'Description *': 'الوصف *',
    'Write video description': 'اكتب وصف الفيديو',
    'Category *': 'الفئة *',
    'Select a category': 'اختر فئة',
    'Author *': 'المؤلف *',
    'Enter author name': 'أدخل اسم المؤلف',
    'Duration': 'المدة',
    'Enter video duration (e.g., 5:30)': 'أدخل مدة الفيديو (مثال: 5:30)',
    'Video *': 'الفيديو *',
    'Upload Video': 'رفع الفيديو',
    'Thumbnail *': 'الصورة المصغرة *',
    'Upload Thumbnail': 'رفع الصورة المصغرة',
    'Publish Video': 'نشر الفيديو',
    'Publishing...': 'جاري النشر...',
    'Uploading...': 'جاري الرفع...',
    'Video Published Successfully!': 'تم نشر الفيديو بنجاح!',
    'Your video has been published successfully.': 'تم نشر فيديوك بنجاح.',
    'Please fill all required fields': 'يرجى ملء جميع الحقول المطلوبة',
    'Please add a thumbnail': 'يرجى إضافة صورة مصغرة',
    'Please upload a video': 'يرجى رفع فيديو',
    'Error saving video:': 'خطأ في حفظ الفيديو:',
    'Upload widget is still loading. Please try again.': 'أداة التحميل لا تزال تحمّل. يرجى المحاولة مرة أخرى.',
    'Field Report': 'تقرير ميداني',
    'Documentary': 'فيلم وثائقي',
    'Interview': 'مقابلة',
    'Training': 'تدريب',
    'Statement': 'بيان',
    'Remove Video': 'إزالة الفيديو',
    'Video uploaded successfully': 'تم رفع الفيديو بنجاح',
    'Upload progress': 'تقدم الرفع',
    'Choose Video File': 'اختر ملف الفيديو',
    'Video preview': 'معاينة الفيديو',
    'Processing video...': 'جاري معالجة الفيديو...',
    'Video file is too large': 'ملف الفيديو كبير جداً',
    'Supported formats: MP4, MOV, AVI, MKV, WebM': 'الصيغ المدعومة: MP4, MOV, AVI, MKV, WebM',
    'Max file size: 500MB': 'الحد الأقصى لحجم الملف: 500 ميجابايت'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const toggleLanguage = () => {
    setIsArabic(!isArabic);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => {
      console.log('Cloudinary widget loaded successfully');
      setWidgetLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Cloudinary widget');
      setError('Failed to load upload widget. Please refresh the page.');
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const openVideoUploadWidget = () => {
    if (!widgetLoaded) {
      setError(translateText("Upload widget is still loading. Please try again."));
      return;
    }

    console.log('Opening video upload widget...');
    
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'drl2kb984',
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'salafcore',
        sources: ['local', 'url', 'camera'],
        multiple: false,
        clientAllowedFormats: ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'],
        maxFileSize: 500000000, // 500MB
        resourceType: 'video',
        showAdvancedOptions: true,
        singleUploadAutoClose: false,
        showUploadMoreButton: false,
        styles: {
          palette: {
            window: "#FFFFFF",
            windowBorder: "#dc3545",
            tabIcon: "#dc3545",
            menuIcons: "#dc3545",
            textDark: "#000000",
            textLight: "#FFFFFF",
            link: "#dc3545",
            action: "#dc3545",
            inactiveTabIcon: "#666666",
            error: "#dc3545",
            inProgress: "#0078FF",
            complete: "#20B832",
            sourceBg: "#E4EBF1"
          },
          fonts: {
            default: null,
            "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif": {
              url: null,
              active: true
            }
          }
        }
      },
      (error, result) => {
        console.log('Cloudinary callback:', { error, result });
        
        if (error) {
          console.error('Cloudinary upload error:', error);
          setError(`Upload failed: ${error.message || 'Unknown error'}`);
          setIsUploading(false);
          return;
        }

        if (result && result.event === 'success') {
          console.log('Upload successful:', result.info);
          setVideoUrl(result.info.secure_url);
          setVideoData(result.info);
          
          // Extract duration if available
          if (result.info.duration) {
            const duration = Math.round(result.info.duration);
            const minutes = Math.floor(duration / 60);
            const seconds = duration % 60;
            setFormData(prev => ({
              ...prev,
              duration: `${minutes}:${seconds.toString().padStart(2, '0')}`
            }));
          }
          
          setError(null);
          setIsUploading(false);
          setUploadProgress(100);
        } else if (result && result.event === 'upload-added') {
          console.log('Upload started:', result);
          setIsUploading(true);
          setUploadProgress(0);
        } else if (result && result.event === 'progress') {
          const progress = Math.round(result.info.progress);
          console.log('Upload progress:', progress);
          setUploadProgress(progress);
        } else if (result && result.event === 'close') {
          console.log('Widget closed');
          setIsUploading(false);
        }
      }
    );

    widget.open();
  };

  const openThumbnailUploadWidget = () => {
    if (!widgetLoaded) {
      setError(translateText("Upload widget is still loading. Please try again."));
      return;
    }

    console.log('Opening thumbnail upload widget...');
    
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'drl2kb984',
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'salafcore',
        sources: ['local', 'url', 'camera'],
        multiple: false,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif'],
        maxFileSize: 5000000, // 5MB
        resourceType: 'image',
        showAdvancedOptions: true,
        singleUploadAutoClose: true,
        showUploadMoreButton: false,
        styles: {
          palette: {
            window: "#FFFFFF",
            windowBorder: "#dc3545",
            tabIcon: "#dc3545",
            menuIcons: "#dc3545",
            textDark: "#000000",
            textLight: "#FFFFFF",
            link: "#dc3545",
            action: "#dc3545",
            inactiveTabIcon: "#666666",
            error: "#dc3545",
            inProgress: "#0078FF",
            complete: "#20B832",
            sourceBg: "#E4EBF1"
          }
        }
      },
      (error, result) => {
        console.log('Thumbnail upload callback:', { error, result });
        
        if (error) {
          console.error('Thumbnail upload error:', error);
          setError(`Thumbnail upload failed: ${error.message || 'Unknown error'}`);
          return;
        }

        if (!error && result && result.event === 'success') {
          console.log('Thumbnail upload successful:', result.info);
          setThumbnail(result.info.secure_url);
          setError(null);
        }
      }
    );

    widget.open();
  };

  const removeThumbnail = () => {
    setThumbnail('');
  };

  const removeVideo = () => {
    setVideoUrl('');
    setVideoData(null);
    setFormData(prev => ({ ...prev, duration: '' }));
    setUploadProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.title || !formData.description || !formData.category || !formData.author) {
      setError(translateText('Please fill all required fields'));
      setLoading(false);
      return;
    }

    if (!thumbnail) {
      setError(translateText('Please add a thumbnail'));
      setLoading(false);
      return;
    }

    if (!videoUrl) {
      setError(translateText('Please upload a video'));
      setLoading(false);
      return;
    }

    try {
      const token = uuidv4();

      const videoDoc = {
        title: formData.title,
        content: formData.description,
        category: formData.category,
        author: formData.author,
        excerpt: formData.description.substring(0, 150) + '...',
        tags: ['video', formData.category],
        featuredImage: thumbnail,
        videoUrl: videoUrl,
        duration: formData.duration,
        token: token,
        status: 'published',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        views: 0,
        likes: 0,
        isVideo: true,
        videoType: 'cloudinary',
        fileSize: videoData?.bytes || 0,
        resolution: videoData?.width && videoData?.height ? `${videoData.width}x${videoData.height}` : 'Unknown',
        format: videoData?.format || 'Unknown'
      };

      console.log('Saving video to Firestore:', videoDoc);

      // Save to videos collection
      await addDoc(collection(db1, 'videos'), videoDoc);

      // Save token reference
      await setDoc(doc(db1, 'video_tokens', token), {
        title: formData.title,
        createdAt: serverTimestamp(),
        status: 'published',
        type: 'video',
        videoUrl: videoUrl
      });

      setSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          title: '',
          description: '',
          category: 'field_report',
          author: 'Al-Majd Media',
          duration: '',
        });
        setThumbnail('');
        setVideoUrl('');
        setVideoData(null);
        setUploadProgress(0);
      }, 3000);
      
    } catch (err) {
      console.error('Error saving video:', err);
      setError(`${translateText('Error saving video:')} ${err.message}`);
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      padding: '0',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh'
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '3rem 2rem',
      borderRadius: '20px',
      boxShadow: '0 15px 40px rgba(220, 53, 69, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(220, 53, 69, 0.1)',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#1a1a1a',
      padding: '120px 20px 40px',
      background: `linear-gradient(135deg, 
        rgba(220, 53, 69, 0.05) 0%, 
        rgba(220, 53, 69, 0.1) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #dc3545 0%, #1a1a1a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 5px 15px rgba(220, 53, 69, 0.2)',
      letterSpacing: '-1px',
      lineHeight: '1.2'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(220, 53, 69, 0.1)',
      borderRadius: '50%'
    }
  };

  return (
    <div style={styles.container}>
      {/* Floating Background Shapes */}
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(220, 53, 69, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(220, 53, 69, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(220, 53, 69, 0.03)'}}></div>
      </div>

      {/* Language Toggle Button */}
      <div style={styles.languageToggle}>
        <button 
          onClick={toggleLanguage}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1a1a1a';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(26, 26, 26, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#dc3545';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>{translateText('Video Upload')}</h1>
        <p style={{ color: '#dc3545', fontSize: '1.2rem' }}>
          {translateText('Upload your video content')}
        </p>
      </header>

      {/* Main Content */}
      <div style={styles.content}>
        <Card className="shadow-lg" style={{
          border: 'none',
          borderRadius: '12px',
          backgroundColor: '#FFFFFF'
        }}>
          <Card.Body style={{ padding: '30px' }}>
            <div className="text-center mb-4">
              <h2 style={{ color: '#dc3545', fontWeight: '700' }}>
                {translateText('Video Upload')}
              </h2>
              <p style={{ color: '#666' }}>
                {translateText('Upload your video content')}
              </p>
            </div>

            {success ? (
              <Alert variant="light" style={{ 
                border: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                color: '#dc3545'
              }}>
                <div className="d-flex align-items-center">
                  <div className="rounded p-2 me-3" style={{ 
                    backgroundColor: '#dc3545' 
                  }}>
                    <i className="bi bi-check-circle-fill text-light fs-4"></i>
                  </div>
                  <div>
                    <h4 style={{ color: '#dc3545' }}>{translateText('Video Published Successfully!')}</h4>
                    <p style={{ color: '#dc3545' }}>{translateText('Your video has been published successfully.')}</p>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => setSuccess(false)}
                      style={{ color: '#dc3545', borderColor: '#dc3545' }}
                    >
                      {translateText('Upload Another Video')}
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : (
              <Form onSubmit={handleSubmit}>
                {error && (
                  <Alert variant="danger" style={{ 
                    borderRadius: '8px',
                    border: 'none'
                  }}>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </Alert>
                )}

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Title *')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder={translateText('Enter video title')}
                    style={{ 
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '8px',
                      padding: '10px 15px'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Description *')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    placeholder={translateText('Write video description')}
                    style={{ 
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      resize: 'none'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Category *')}</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                    style={{ 
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '8px',
                      padding: '10px 15px'
                    }}
                  >
                    <option value="field_report">{translateText('Field Report')}</option>
                    <option value="documentary">{translateText('Documentary')}</option>
                    <option value="interview">{translateText('Interview')}</option>
                    <option value="training">{translateText('Training')}</option>
                    <option value="statement">{translateText('Statement')}</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Author *')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    required
                    placeholder={translateText('Enter author name')}
                    style={{ 
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '8px',
                      padding: '10px 15px'
                    }}
                  />
                </Form.Group>

                {/* Video Upload Section */}
                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Video *')}</Form.Label>
                  
                  <div className="mb-3">
                    <Button 
                      variant="outline-primary" 
                      onClick={openVideoUploadWidget}
                      type="button"
                      className="d-flex align-items-center me-2 mb-2"
                      disabled={isUploading}
                      style={{ 
                        color: '#dc3545', 
                        borderColor: '#dc3545',
                        borderRadius: '8px',
                        padding: '12px 20px',
                        fontWeight: '600'
                      }}
                    >
                      <i className="bi bi-cloud-arrow-up-fill me-2 fs-5"></i>
                      {isUploading ? translateText('Uploading...') : translateText('Upload Video')}
                    </Button>
                    
                    {videoUrl && (
                      <Button 
                        variant="outline-danger" 
                        onClick={removeVideo}
                        type="button"
                        className="d-flex align-items-center mb-2"
                        style={{ 
                          borderRadius: '8px',
                          padding: '12px 20px',
                          fontWeight: '600'
                        }}
                      >
                        <i className="bi bi-trash me-2"></i>
                        {translateText('Remove Video')}
                      </Button>
                    )}
                  </div>

                  {isUploading && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small style={{ color: '#666' }}>{translateText('Upload progress')}</small>
                        <small style={{ color: '#666' }}>{uploadProgress}%</small>
                      </div>
                      <ProgressBar 
                        now={uploadProgress} 
                        style={{ 
                          height: '8px',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                  )}
                  
                  {videoUrl && (
                    <div className="position-relative mt-3">
                      <div className="mb-2">
                        <small style={{ color: '#666', fontWeight: '600' }}>{translateText('Video preview')}:</small>
                      </div>
                      <video 
                        controls
                        style={{ 
                          width: '100%',
                          maxHeight: '300px',
                          border: '2px solid rgba(220, 53, 69, 0.2)',
                          borderRadius: '8px',
                          backgroundColor: '#000'
                        }}
                      >
                        <source src={videoUrl} type="video/mp4" />
                        {translateText('Your browser does not support the video tag.')}
                      </video>
                      <div className="mt-2 text-success">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        {translateText('Video uploaded successfully')}
                      </div>
                    </div>
                  )}

                  <Form.Text className="text-muted">
                    {translateText('Supported formats: MP4, MOV, AVI, MKV, WebM')} | {translateText('Max file size: 500MB')}
                  </Form.Text>
                </Form.Group>

                {/* Thumbnail Upload Section */}
                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Thumbnail *')}</Form.Label>
                  <div className="mb-3">
                    <Button 
                      variant="outline-primary" 
                      onClick={openThumbnailUploadWidget}
                      type="button"
                      className="d-flex align-items-center me-2"
                      style={{ 
                        color: '#dc3545', 
                        borderColor: '#dc3545',
                        borderRadius: '8px',
                        padding: '12px 20px',
                        fontWeight: '600'
                      }}
                    >
                      <i className="bi bi-image-fill me-2"></i>
                      {translateText('Upload Thumbnail')}
                    </Button>
                  </div>
                  
                  {thumbnail && (
                    <div className="position-relative mt-3" style={{ maxWidth: '300px' }}>
                      <div className="mb-2">
                        <small style={{ color: '#666', fontWeight: '600' }}>{translateText('Thumbnail preview')}:</small>
                      </div>
                      <Image 
                        src={thumbnail} 
                        thumbnail
                        style={{ 
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover', 
                          border: '2px solid rgba(220, 53, 69, 0.2)',
                          borderRadius: '8px'
                        }}
                      />
                      <Button
                        variant="light"
                        size="sm"
                        className="position-absolute top-0 end-0 m-2"
                        style={{ 
                          width: '32px', 
                          height: '32px',
                          borderRadius: '50%',
                          border: '1px solid rgba(220, 53, 69, 0.2)',
                          color: '#dc3545',
                          backgroundColor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}
                        onClick={removeThumbnail}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Duration')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    placeholder={translateText('Enter video duration (e.g., 5:30)')}
                    style={{ 
                      border: '1px solid rgba(220, 53, 69, 0.2)',
                      borderRadius: '8px',
                      padding: '10px 15px'
                    }}
                  />
                  <Form.Text className="text-muted">
                    {translateText('Duration will be automatically detected from the video file')}
                  </Form.Text>
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading || isUploading}
                  className="w-100 py-3 mt-3"
                  style={{ 
                    backgroundColor: '#dc3545', 
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '1.1rem',
                    opacity: (loading || isUploading) ? 0.7 : 1
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" className="me-2" />
                      {translateText('Publishing...')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill me-2"></i>
                      {translateText('Publish Video')}
                    </>
                  )}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default VideoUploadPage;
